import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getOriginalPDF } from 'support/helpers/messages/messages';
import { isMessageV2DTO } from 'support/helpers/transactions/transactions';
import { MessageState, PartnerDTO, TransactionOriginApp } from 'support/types';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';
import { Sidebar } from 'components/Sidebar/Sidebar';
import { PDFViewerPanel } from 'components/WebEDI/PDFViewerPanel/PDFViewerPanel';

import AgentsTab from './AgentsTab/AgentsTab';
import { OverviewTab } from './components/OverviewTab';
import { ResizablePanelHandle } from './components/ResizablePanelHandle';
import { TabBar, TabBarItem } from './components/TabBar';
import { DocumentDTO, WebEDISidebarTabs } from './types';
import { useResizablePanel } from './useResizablePanel';

export { WebEDIDocumentPropertiesKeys as DocumentPropertiesKeys } from './components/WebEDIDocumentProperties/WebEDIDocumentPropertiesKeys';

type PDFViewerState = {
  url: string;
  filename: string;
};

type WebEDISidebarProps = {
  document: DocumentDTO | undefined;
  documentOriginApp: TransactionOriginApp;
  relatedTransactions: Array<DocumentDTO> | undefined;
  currentPartner: PartnerDTO | undefined;
  children?: ReactNode;
  isSenderEnvelope?: boolean;
  showAgentsTab?: boolean;
  hasIdentifierErrors?: boolean;
  hasEnrichmentApplied?: boolean;
  isValidationComplete?: boolean;
  initialDefaultTab?: WebEDISidebarTabs;
  isLoading?: boolean;
};

export const WebEDISidebar = ({
  children,
  document,
  documentOriginApp,
  relatedTransactions,
  currentPartner,
  isSenderEnvelope,
  showAgentsTab,
  hasIdentifierErrors,
  hasEnrichmentApplied,
  isValidationComplete,
  initialDefaultTab,
  isLoading,
}: WebEDISidebarProps) => {
  const { t } = useTranslation();
  const message = document && isMessageV2DTO(document) ? document : undefined;

  // `activeTab === null` means the initial default hasn't been committed yet for the
  // current document. Driven by `isLoading`: while loading we stay null and render a
  // spinner so the user doesn't see the wrong tab before the caller resolves defaults.
  const [activeTab, setActiveTab] = useState<WebEDISidebarTabs | null>(() =>
    isLoading ? null : (initialDefaultTab ?? WebEDISidebarTabs.OVERVIEW),
  );
  const lastDocumentId = useRef<string | undefined>(document?.id);

  useEffect(() => {
    if (document?.id !== lastDocumentId.current) {
      lastDocumentId.current = document?.id;
      setActiveTab(null);
    }
  }, [document?.id]);

  // Commit the initial default exactly once per document. Subsequent prop changes —
  // `initialDefaultTab` flipping, `isLoading` toggling back to true — don't flip the
  // committed tab. User clicks set `activeTab` directly; this effect won't fire again
  // because `activeTab` is no longer null.
  useEffect(() => {
    if (activeTab === null && !isLoading) {
      setActiveTab(initialDefaultTab ?? WebEDISidebarTabs.OVERVIEW);
    }
  }, [activeTab, isLoading, initialDefaultTab]);

  // If the agents tab becomes unavailable while it's active, fall back to overview.
  useEffect(() => {
    if (!showAgentsTab && activeTab === WebEDISidebarTabs.AGENTS) {
      setActiveTab(WebEDISidebarTabs.OVERVIEW);
    }
  }, [showAgentsTab, activeTab]);

  const originalPDF = getOriginalPDF(documentOriginApp, message);
  const shouldAutoOpenPDF =
    !!originalPDF &&
    (message?.internal_state === MessageState.pendingReview ||
      message?.internal_state === MessageState.validated_for_destination ||
      message?.internal_state === MessageState.validation_for_destination_failed);

  const [pdfViewer, setPdfViewer] = useState<PDFViewerState | null>(shouldAutoOpenPDF ? originalPDF : null);
  const { panelWidth, isCollapsed, handleMouseDown, expand, restoreNav } = useResizablePanel(!!pdfViewer);

  if (pdfViewer) {
    return (
      <aside
        className="relative flex flex-col border-l border-gray-200"
        style={{ width: panelWidth, height: `var(--content-height)` }}
      >
        <ResizablePanelHandle onMouseDown={handleMouseDown} />
        <PDFViewerPanel
          pdfUrl={pdfViewer.url}
          filename={pdfViewer.filename}
          onClose={() => {
            setPdfViewer(null);
            restoreNav();
          }}
          isCollapsed={isCollapsed}
          onExpand={expand}
        />
      </aside>
    );
  }

  if (activeTab === null) {
    return (
      <Sidebar bottomSlot={children}>
        <div data-testid="webedi-sidebar-loading" className="flex justify-center p-8">
          <LoadingSpinner />
        </div>
      </Sidebar>
    );
  }

  const tabs: Array<TabBarItem<WebEDISidebarTabs>> = [
    { id: WebEDISidebarTabs.OVERVIEW, label: t('webedi:sidebar.tabs.overview') },
    ...(showAgentsTab ? [{ id: WebEDISidebarTabs.AGENTS, label: t('webedi:sidebar.tabs.agents') }] : []),
  ];

  return (
    <Sidebar
      header={<TabBar tabs={tabs} activeTab={activeTab} onClickTab={(tab) => setActiveTab(tab)} />}
      bottomSlot={children}
      activeTabId={activeTab}
    >
      {activeTab === WebEDISidebarTabs.OVERVIEW && (
        <OverviewTab
          document={document}
          documentOriginApp={documentOriginApp}
          relatedTransactions={relatedTransactions}
          currentPartner={currentPartner}
          isSenderEnvelope={isSenderEnvelope}
          onViewPDF={(url, filename) => setPdfViewer({ url, filename })}
        />
      )}
      {activeTab === WebEDISidebarTabs.AGENTS && (
        <AgentsTab
          message={message}
          hasIdentifierErrors={hasIdentifierErrors ?? false}
          hasEnrichmentApplied={hasEnrichmentApplied ?? false}
          isValidationComplete={isValidationComplete ?? true}
        />
      )}
    </Sidebar>
  );
};
